import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const PERIODS = [
  { label: '15 dias', days: 15 }, { label: '30 dias', days: 30 },
  { label: '60 dias', days: 60 }, { label: '90 dias', days: 90 },
  { label: '120 dias', days: 120 }, { label: '6 meses', days: 180 },
  { label: '1 ano', days: 365 }
];

export default function Reports() {
  const [proposals, setProposals] = useState([]);
  const [period, setPeriod] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/proposals/').then(r => setProposals(r.data)).finally(() => setLoading(false));
  }, []);

  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - period);

  const filtered = proposals.filter(p => new Date(p.created_at) >= cutoff);
  const approved = filtered.filter(p => ['aprovado', 'em_andamento', 'concluido', 'pago'].includes(p.status));
  const paid = filtered.filter(p => p.status === 'pago');

  const buildData = () => {
    const map = {};
    filtered.forEach(p => {
      const d = new Date(p.created_at);
      let key;
      if (period <= 30) key = `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}`;
      else {
        const wk = Math.ceil((d.getDate() + new Date(d.getFullYear(), d.getMonth(), 1).getDay()) / 7);
        key = `Sem ${wk} ${String(d.getMonth()+1).padStart(2,'0')}`;
      }
      if (!map[key]) map[key] = { date: key, gerado: 0, aprovado: 0, pago: 0 };
      map[key].gerado += p.valor;
      if (['aprovado','em_andamento','concluido','pago'].includes(p.status)) map[key].aprovado += p.valor;
      if (p.status === 'pago') map[key].pago += p.valor;
    });
    return Object.values(map);
  };

  const chartData = buildData();
  const totalGerado = filtered.reduce((s, p) => s + p.valor, 0);
  const totalAprovado = approved.reduce((s, p) => s + p.valor, 0);
  const totalPago = paid.reduce((s, p) => s + p.valor, 0);

  const customTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-xs space-y-1">
        <div className="text-zinc-400 font-medium">{label}</div>
        {payload.map(p => (
          <div key={p.dataKey} style={{ color: p.color }}>{p.name}: {brl(p.value)}</div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-2xl font-bold text-white">Relatórios</h1>
        <div className="flex gap-1.5 flex-wrap bg-zinc-900 border border-zinc-800 rounded-xl p-1">
          {PERIODS.map(p => (
            <button key={p.days} onClick={() => setPeriod(p.days)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                period === p.days ? 'bg-orange-500 text-white' : 'text-zinc-400 hover:text-white'
              }`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'Total gerado', value: brl(totalGerado), color: 'text-white' },
          { label: 'Total aprovado', value: brl(totalAprovado), color: 'text-green-400' },
          { label: 'Total recebido', value: brl(totalPago), color: 'text-emerald-400' },
        ].map(k => (
          <div key={k.label} className="stat-card">
            <div className="text-xs text-zinc-500 mb-2">{k.label}</div>
            <div className={`font-mono font-bold text-xl ${k.color}`}>{k.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <h3 className="font-medium text-white mb-4">Faturamento por período</h3>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-7 h-7 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="date" tick={{ fill: '#71717a', fontSize: 10 }} />
              <YAxis tick={{ fill: '#71717a', fontSize: 10 }} tickFormatter={v => `R$${(v/1000).toFixed(0)}k`} />
              <Tooltip content={customTooltip} />
              <Bar dataKey="gerado" name="Gerado" fill="#3f3f46" radius={[3,3,0,0]} />
              <Bar dataKey="aprovado" name="Aprovado" fill="#22c55e" radius={[3,3,0,0]} />
              <Bar dataKey="pago" name="Pago" fill="#f97316" radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
